import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createQuoteSchema, formatMoney, type Customer, type Sku } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { WIDE_COLUMN } from '@/components/layout';
import { BackLink } from '@/components/DetailRow';
import { StatusPill } from '@/components/StatusPill';
import { FormError, TextField, TextareaField } from '@/components/form';
import { useCreateQuote, useProductCatalog } from '@/data/hooks';
import { CustomerPicker } from '@/features/orders/CustomerPicker';
import { OrderLineRow, type DraftLine } from '@/features/orders/OrderLineRow';
import { cartSubtotalMinorUsd, type CartLine } from '@/features/orders/cart';

interface CotizacionCreatePageProps {
  /** Group root used for the back link + the new quote's detail link. */
  basePath: string;
  /** Where the cotizaciones list lives under this group (for the back link). */
  quotesPath: string;
}

let lineSeq = 0;
const newLine = (): DraftLine => ({ key: `q${++lineSeq}`, productId: '', skuId: '', quantity: 1 });

/**
 * Crear cotización. A sales rep / admin: (1) picks a customer, (2) adds line
 * items (product → SKU → quantity), (3) optionally sets a validity window +
 * notes, sees the live USD total, then submits. Unlike a pedido there is NO
 * exchange rate here — quotes are USD and reserve no stock; the rate is supplied
 * later only if the quote is converted to an order. Validation uses the shared
 * `createQuoteSchema`; the backend remains authoritative on price.
 */
export function CotizacionCreatePage({ basePath, quotesPath }: CotizacionCreatePageProps) {
  const navigate = useNavigate();
  const productsQuery = useProductCatalog();
  const products = useMemo(
    () => (productsQuery.data?.items ?? []).filter((p) => p.active),
    [productsQuery.data],
  );
  const mutation = useCreateQuote();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [lines, setLines] = useState<DraftLine[]>(() => [newLine()]);
  const [validDaysText, setValidDaysText] = useState('');
  const [notes, setNotes] = useState('');
  // Resolved SKU per line key (reported by each row's query) for live pricing.
  const [skuByLine, setSkuByLine] = useState<Record<string, Sku | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const onSkuResolved = useCallback((key: string, sku: Sku | undefined) => {
    setSkuByLine((prev) => (prev[key] === sku ? prev : { ...prev, [key]: sku }));
  }, []);

  const patchLine = (key: string, patch: Partial<Omit<DraftLine, 'key'>>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
    setSkuByLine((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Build the priced cart from lines whose SKU resolved + quantity > 0.
  const cart: CartLine[] = useMemo(
    () =>
      lines
        .map((l) => ({ sku: skuByLine[l.key], quantity: l.quantity }))
        .filter((c): c is CartLine => Boolean(c.sku) && c.quantity > 0),
    [lines, skuByLine],
  );
  const subtotalUsd = cartSubtotalMinorUsd(cart);

  // Submittable lines: valid skuId + positive quantity (what goes to the API).
  const quoteItems = useMemo(
    () =>
      lines
        .filter((l) => l.skuId && l.quantity > 0)
        .map((l) => ({ skuId: l.skuId, quantity: l.quantity })),
    [lines],
  );

  const canSubmit = Boolean(customer) && quoteItems.length > 0 && !mutation.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!customer) {
      setFormError('Selecciona un cliente.');
      return;
    }
    const trimmedDays = validDaysText.trim();
    const candidate = {
      customerId: customer.customerId,
      items: quoteItems,
      validUntilDays: trimmedDays ? Number(trimmedDays) : undefined,
      notes: notes.trim() || undefined,
    };
    const parsed = createQuoteSchema.safeParse(candidate);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Revisa los datos de la cotización.');
      return;
    }
    mutation.mutate(parsed.data, {
      onSuccess: (created) => navigate(`${basePath}/${created.quoteId}`),
    });
  };

  return (
    <div className={`flex flex-col gap-6 ${WIDE_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={quotesPath}>Cotizaciones</BackLink>
        <PageHeader
          title="Crear cotización"
          description="Arma una cotización para un cliente. No reserva inventario."
        />
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
        {/* 1. Cliente */}
        <Card>
          <CardHeader>
            <CardTitle>1. Cliente</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {customer ? (
              <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">{customer.name}</span>
                    <StatusPill kind="account" value={customer.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-4 text-xs text-text-muted">
                    <span>{customer.rif}</span>
                    <span>Saldo: {formatMoney(customer.openBalanceMinorUsd)}</span>
                    <span>Crédito disp.: {formatMoney(customer.availableCreditMinorUsd)}</span>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setCustomer(null)}>
                  Cambiar
                </Button>
              </div>
            ) : (
              <CustomerPicker selectedId="" onSelect={setCustomer} />
            )}
          </CardContent>
        </Card>

        {/* 2. Productos */}
        <Card>
          <CardHeader>
            <CardTitle>2. Productos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 pt-0">
            {productsQuery.isError ? (
              <p className="text-sm text-danger">
                No se pudo cargar el catálogo. Recarga la página.
              </p>
            ) : null}
            {lines.map((line, index) => (
              <OrderLineRow
                key={line.key}
                index={index}
                line={line}
                products={products}
                onChange={(patch) => patchLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
                onSkuResolved={onSkuResolved}
                removable={lines.length > 1}
              />
            ))}
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLines((p) => [...p, newLine()])}
              >
                Agregar línea
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 3. Vigencia, notas y total */}
        <Card>
          <CardHeader>
            <CardTitle>3. Vigencia y total</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5 pt-0">
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label="Validez (días)"
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                step={1}
                placeholder="Ej. 30"
                hint="Días que la cotización permanece vigente. Opcional."
                value={validDaysText}
                onChange={(e) => setValidDaysText(e.target.value)}
              />
            </div>
            <TextareaField
              label="Notas"
              placeholder="Indicaciones opcionales para esta cotización."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="flex flex-col gap-2 rounded-md bg-surface-muted p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">Total (USD)</span>
                <span className="text-2xl font-semibold tracking-tight text-text-primary">
                  {formatMoney(subtotalUsd)}
                </span>
              </div>
              <p className="text-xs text-text-muted">
                La cotización se expresa en USD. La tasa en VES se fija solo al convertirla en
                pedido.
              </p>
            </div>

            <FormError error={mutation.error} />
            {formError ? (
              <p className="text-sm text-danger" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => navigate(quotesPath)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? 'Creando…' : 'Crear cotización'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
